/**
 * AddSectionPalette — canvas-level palette of draggable tiles.
 *   • Sections: drag onto a page → adds a section block.
 *   • Pages:    drag onto the canvas → creates a new page.
 * AddDragContext handles the pointer lifecycle + drop; the per-page
 * "+ Add section" menu (SectionPalette) remains as the section click path.
 *
 * Responsive: a floating panel on desktop, a bottom tray on mobile
 * (collapsed dock → tap to reveal horizontally-scrolling tile strips).
 *
 * Positioned absolutely within the (relative) canvas container.
 */
import { useState } from "react";
import { css, html } from "react-strict-dom";
import { colors } from "@duro-app/tokens/tokens/colors.css";
import { microSpacing, radii } from "@duro-app/tokens/tokens/spacing.css";
import { borders } from "@duro-app/tokens/tokens/borders.css";
import { shadows } from "@duro-app/tokens/tokens/shadows.css";
import { typography } from "@duro-app/tokens/tokens/typography.css";

import { useIsMobile } from "./useIsMobile.js";
import { useCanvasZoomValue, WIREFRAME_MAX_ZOOM } from "./canvasZoom.js";
import { useAddDrag, type DraggingInfo } from "./AddDragContext.js";
import { useDockablePanel } from "./DockablePanel.js";
import { DockDropPreview } from "./DockDropPreview.js";
import { DropPreview } from "./dropPreviews.js";
import { SECTION_TEMPLATES } from "./sectionTemplates.js";
import { PAGE_TEMPLATES } from "./pageTemplates.js";
import { dsWidgetTiles, type WidgetPreset } from "./widgetPresets.js";
import { usePaletteDesignSystemName } from "./paletteDesignSystem.js";
import { sizes } from "@duro-app/tokens/tokens/sizes.css";
import { layers } from "@duro-app/tokens/tokens/layers.css";

interface Tile {
  readonly kind: "section" | "page" | "widget";
  readonly id: string;
  readonly icon: string;
  readonly label: string;
  readonly hint?: string;
  /** For kind "widget": the content type it inserts. */
  readonly widgetType?: string;
  /** For a design-system widget: what the drop inserts (fresh per drag). */
  readonly preset?: () => WidgetPreset;
}

interface Group {
  readonly title: string;
  /** Widget groups need a mounted section grid to land in. */
  readonly widgets: boolean;
  readonly tiles: Tile[];
}

const DS_TILES: Tile[] = dsWidgetTiles().map((t) => ({
  kind: "widget" as const,
  id: t.id,
  icon: t.icon,
  label: t.label,
  widgetType: t.preset().contentType,
  preset: t.preset,
  hint: "Drag into a section",
}));

/** Basics, then the site's design system (named after it), sections, pages. */
function groups(designSystemName: string): Group[] {
  return [
    { title: "Basics", widgets: true, tiles: BASIC_TILES },
    { title: designSystemName, widgets: true, tiles: DS_TILES },
    ...STRUCTURE_GROUPS,
  ];
}

const BASIC_TILES: Tile[] = [
  {
    kind: "widget" as const,
    id: "text",
    icon: "🔤",
    label: "Text",
    widgetType: "text" as const,
    hint: "Drag into a section",
  },
  {
    kind: "widget" as const,
    id: "image",
    icon: "🖼️",
    label: "Image",
    widgetType: "image" as const,
    hint: "Drag into a section",
  },
  {
    kind: "widget" as const,
    id: "video",
    icon: "🎬",
    label: "Video",
    widgetType: "video" as const,
    hint: "Drag into a section",
  },
  {
    kind: "widget" as const,
    id: "audio",
    icon: "🎵",
    label: "Audio",
    widgetType: "audio" as const,
    hint: "Drag into a section",
  },
  {
    kind: "widget" as const,
    id: "spacer",
    icon: "↕️",
    label: "Spacer",
    widgetType: "spacer" as const,
    hint: "Blank space — resize to set height",
  },
];

const STRUCTURE_GROUPS: Group[] = [
  {
    title: "Sections",
    widgets: false,
    tiles: SECTION_TEMPLATES.map((t) => ({
      kind: "section" as const,
      id: t.id,
      icon: t.icon,
      label: t.label,
      hint: t.description,
    })),
  },
  {
    title: "Pages",
    widgets: false,
    tiles: PAGE_TEMPLATES.map((t) => ({
      kind: "page" as const,
      id: t.id,
      icon: t.icon,
      label: t.label,
    })),
  },
];

// react-strict-dom (`html` + `css.create`) so the draggable tile compiles to
// StyleX on web and ports to native. NOT @duro-app/ui Button: the DS Button
// only exposes onClick, but the tile needs onPointerDown for the drag lifecycle
// (AddDragContext). RSD's html.button carries arbitrary DOM handlers, so it
// fits where the DS Button can't. (The web-only `title` tooltip is dropped —
// RSD has no `title`; the palette footer already explains the drag gesture.)
const tileStyles = css.create({
  tile: {
    flexShrink: 0,
    minWidth: sizes.paletteMinW,
    paddingTop: "0.5rem",
    paddingBottom: "0.5rem",
    paddingLeft: "0.5rem",
    paddingRight: "0.5rem",
    backgroundColor: colors.bgCard,
    borderWidth: borders.hairline,
    borderStyle: "solid",
    borderColor: colors.border,
    borderRadius: radii.sm,
    cursor: "grab",
    textAlign: "left",
    fontSize: typography.fontSizeXs,
    touchAction: "none",
  },
  label: {
    marginTop: "0.3rem",
    fontWeight: typography.fontWeightMedium,
    color: colors.text,
  },
});

function PaletteTile({ tile }: { tile: Tile }) {
  const { start } = useAddDrag();
  const dragInfo = (): DraggingInfo => {
    const preset = tile.preset?.();
    return {
      kind: tile.kind,
      templateId: tile.id,
      icon: tile.icon,
      label: tile.label,
      ...(tile.widgetType ? { widgetType: tile.widgetType } : {}),
      ...(preset
        ? {
            contentProps: preset.contentProps,
            size: { w: preset.w, h: preset.h },
            constraints: preset.constraints,
          }
        : {}),
    };
  };
  return (
    <html.button
      type="button"
      onPointerDown={(e: React.PointerEvent) => start(dragInfo(), e)}
      style={tileStyles.tile}
    >
      <DropPreview
        kind={tile.kind}
        templateId={tile.id}
        widgetType={tile.widgetType}
        label={tile.label}
        width={100}
        cropHeight={58}
      />
      <html.div style={tileStyles.label}>{tile.label}</html.div>
    </html.button>
  );
}

// Chrome styles (the tiles have their own `tileStyles` above). react-strict-dom
// requires longhand properties, so shorthands are expanded; dynamic values
// (drag position) go through the parameterized `panelPos` below.
const chrome = css.create({
  groupTitle: {
    fontSize: "0.65rem",
    fontWeight: typography.fontWeightSemibold,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: colors.textMuted,
    marginTop: 0,
    marginRight: 0,
    marginBottom: "0.375rem",
    marginLeft: 0,
  },
  body: {
    paddingTop: "0.5rem",
    paddingRight: "0.5rem",
    paddingBottom: "0.5rem",
    paddingLeft: "0.5rem",
    // overscroll-contain stops a scroll that reaches the end here from chaining
    // out to the document.
    minHeight: 0,
    overflowY: "auto",
    overscrollBehavior: "contain",
  },
  group: { marginBottom: "0.5rem" },
  tileGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "0.375rem",
  },
  hint: {
    marginTop: 0,
    marginRight: 0,
    marginBottom: 0,
    marginLeft: 0,
    color: colors.textMuted,
    fontSize: "0.7rem",
  },
  mobileWrap: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: layers.modal,
    display: "flex",
    flexDirection: "column",
  },
  mobileSheet: {
    backgroundColor: colors.bg,
    borderTopWidth: borders.hairline,
    borderTopStyle: "solid",
    borderTopColor: colors.border,
    boxShadow: shadows.sm,
    paddingTop: "0.625rem",
    paddingRight: "0.625rem",
    paddingBottom: "0.625rem",
    paddingLeft: "0.625rem",
    maxHeight: "40vh",
    overflowY: "auto",
  },
  mobileRow: {
    display: "flex",
    gap: "0.5rem",
    overflowX: "auto",
    touchAction: "pan-x",
  },
  mobileBtn: {
    paddingTop: "0.6rem",
    paddingRight: "0.6rem",
    paddingBottom: "0.6rem",
    paddingLeft: "0.6rem",
    backgroundColor: colors.text,
    color: colors.bg,
    borderStyle: "none",
    cursor: "pointer",
    fontSize: "0.8rem",
    fontWeight: typography.fontWeightSemibold,
  },
  panel: {
    position: "absolute",
    width: sizes.sidebarW,
    backgroundColor: colors.bg,
    borderWidth: borders.hairline,
    borderStyle: "solid",
    borderColor: colors.border,
    borderRadius: radii.sm,
    overflow: "hidden",
    // Flex column so the header stays put and the body (below) is the scroller.
    display: "flex",
    flexDirection: "column",
  },
  // pos.y is canvas-relative, 100vh is viewport; +76 leaves room for the app
  // header band + a small bottom gap so the panel stays fully on-screen.
  panelPos: (left: number, top: number, maxHeight: string) => ({
    left,
    top,
    maxHeight,
  }),
  zHi: { zIndex: layers.modal },
  zLo: { zIndex: layers.overlay },
  shadowIdle: { boxShadow: shadows.md },
  // The dock target ring: the accent outline (the soft halo it had was a
  // raw colour with no token).
  shadowDock: {
    outlineWidth: borders.focusRing,
    outlineStyle: "solid",
    outlineColor: colors.accent,
  },
  // Fused header: one draggable row = grip cue + title + collapse chevron.
  header: {
    width: "100%",
    paddingTop: "0.45rem",
    paddingBottom: "0.45rem",
    paddingLeft: "0.6rem",
    paddingRight: "0.4rem",
    backgroundColor: colors.bgCard,
    display: "flex",
    alignItems: "center",
    gap: "0.45rem",
    cursor: "move",
    touchAction: "none",
    userSelect: "none",
    fontSize: "0.8rem",
    fontWeight: typography.fontWeightSemibold,
    color: colors.text,
  },
  headerBorder: {
    borderBottomWidth: borders.hairline,
    borderBottomStyle: "solid",
    borderBottomColor: colors.border,
  },
  grip: { color: colors.border, fontSize: "0.7rem", letterSpacing: "0.1em" },
  chevBtn: {
    marginLeft: "auto",
    borderStyle: "none",
    backgroundColor: { default: "transparent", ":hover": colors.bgCardHover },
    cursor: "pointer",
    color: colors.textMuted,
    fontSize: "0.8rem",
    lineHeight: 1,
    paddingTop: microSpacing.px3,
    paddingBottom: microSpacing.px3,
    paddingLeft: microSpacing.px6,
    paddingRight: microSpacing.px6,
    borderRadius: radii.px6,
  },
});

/**
 * The palette's scrolling tile body (the `open` content of the desktop panel),
 * reused by both the standalone AddSectionPalette and the docked group
 * (PanelDockGroup) so the tiles are identical either way.
 */
/**
 * Groups to show at the current canvas zoom. Below the wireframe threshold
 * sections render as flat SVGs with no mounted nested grid (and header/footer
 * are skeletons too), so a dragged WIDGET has nowhere to land — hide the Widgets
 * group there, keeping Sections/Pages, which suit zoomed-out structural work.
 * Mirrors the Format menu's contextual hide (progressive disclosure — never show
 * a control that can't do anything in the current context).
 */
function useVisibleGroups(): Group[] {
  const zoom = useCanvasZoomValue();
  const all = groups(usePaletteDesignSystemName());
  return zoom < WIREFRAME_MAX_ZOOM ? all.filter((g) => !g.widgets) : all;
}

export function AddPaletteBody() {
  const groups = useVisibleGroups();
  return (
    <html.div style={chrome.body}>
      {groups.map((g) => (
        <html.div key={g.title} style={chrome.group}>
          <html.p style={chrome.groupTitle}>{g.title}</html.p>
          <html.div style={chrome.tileGrid}>
            {g.tiles.map((t) => (
              <PaletteTile key={`${t.kind}-${t.id}`} tile={t} />
            ))}
          </html.div>
        </html.div>
      ))}
      <html.p style={chrome.hint}>
        Drag a section onto a page, or a page onto the canvas.
      </html.p>
    </html.div>
  );
}

export function AddSectionPalette() {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(!isMobile);
  const mobileGroups = useVisibleGroups();
  // Dock behaviour is shared with Format / Media / Layout. `relativeToOffsetParent`
  // because this panel is position:absolute inside the canvas container, so the
  // tear-off drop point (viewport coords) has to be converted through it.
  const {
    grouped,
    docking,
    atFront,
    ref: panelRef,
    pos,
    onHandleDown,
    bringToFront,
  } = useDockablePanel(
    "add",
    { x: 16, y: 16 },
    {
      relativeToOffsetParent: true,
    },
  );

  if (isMobile) {
    return (
      <html.div style={chrome.mobileWrap}>
        {open && (
          <html.div style={chrome.mobileSheet}>
            {mobileGroups.map((g) => (
              <html.div key={g.title} style={chrome.group}>
                <html.p style={chrome.groupTitle}>{g.title}</html.p>
                <html.div style={chrome.mobileRow}>
                  {g.tiles.map((t) => (
                    <PaletteTile key={`${t.kind}-${t.id}`} tile={t} />
                  ))}
                </html.div>
              </html.div>
            ))}
          </html.div>
        )}
        <html.button
          type="button"
          onClick={() => setOpen((v) => !v)}
          style={chrome.mobileBtn}
        >
          ＋ Add {open ? "▾" : "▴"}
        </html.button>
      </html.div>
    );
  }

  // Docked into the tabbed group — PanelDockGroup renders the Add body instead.
  if (grouped) return null;

  // Desktop: floating panel — draggable by the grip handle.
  return (
    <html.div
      ref={panelRef}
      onPointerDown={bringToFront}
      style={[
        chrome.panel,
        chrome.panelPos(pos.x, pos.y, `calc(100vh - ${pos.y + 76}px)`),
        atFront ? chrome.zHi : chrome.zLo,
        docking ? chrome.shadowDock : chrome.shadowIdle,
      ]}
    >
      {/* One fused header: DRAG the panel by this row (grip cue + title), and
          COLLAPSE via the chevron button (it stops the pointer so pressing it
          doesn't start a drag). While another panel is dragged here, the
          drop-preview tab strip stands in (so "Add" doesn't show twice). */}
      {docking ? (
        <DockDropPreview me="add" />
      ) : (
        <html.div
          onPointerDown={onHandleDown}
          style={[chrome.header, open && chrome.headerBorder]}
        >
          <html.span aria-hidden style={chrome.grip}>
            ⠿
          </html.span>
          Add
          <html.button
            type="button"
            onClick={() => setOpen((v) => !v)}
            onPointerDown={(e: React.PointerEvent) => e.stopPropagation()}
            aria-expanded={open}
            aria-label={open ? "Collapse Add" : "Expand Add"}
            style={chrome.chevBtn}
          >
            {open ? "▾" : "▸"}
          </html.button>
        </html.div>
      )}
      {open && <AddPaletteBody />}
    </html.div>
  );
}
